import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Row, Col, Card, CardBody, Badge, Spinner } from "reactstrap";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import { strategyLabel } from "../../constants/strategies";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";
import { formatCurrency, formatCurrencySigned } from "../../utils/currency";

/* ─── New Trade Modal ─────────────────────────────────────────────────────── */

function SymbolSearch({ value, onChange, placeholder = "e.g. AAPL" }) {
  const [query, setQuery]       = useState(value || "");
  const [sugs, setSugs]         = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [busy, setBusy]         = useState(false);
  const debRef                  = useRef(null);
  const wrapRef                 = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleInput = (raw) => {
    const v = raw.toUpperCase();
    setQuery(v);
    onChange(v);
    clearTimeout(debRef.current);
    if (!v) { setSugs([]); setShowDrop(false); return; }
    debRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await axios.get("/alerts/symbols", { params: { q: v } });
        setSugs(res.data || []);
        setShowDrop((res.data || []).length > 0);
      } catch { setSugs([]); }
      finally { setBusy(false); }
    }, 300);
  };

  const pick = (s) => { setQuery(s); onChange(s); setSugs([]); setShowDrop(false); };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        value={query}
        onChange={e => handleInput(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        style={iStyle}
      />
      {busy && <span style={{ position: "absolute", right: 8, top: 8, color: "#aaa", fontSize: 11 }}>…</span>}
      {showDrop && (
        <ul style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000, background: "#fff", border: "1px solid #ddd", borderRadius: 6, margin: 0, padding: 0, listStyle: "none", boxShadow: "0 4px 12px rgba(0,0,0,.12)", maxHeight: 200, overflowY: "auto" }}>
          {sugs.map(s => (
            <li key={s} onMouseDown={() => pick(s)} style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
              onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewTradeModal({ isOpen, onClose, prefillSymbol, username }) {
  const { currencySymbol } = useAccount();
  const [tab, setTab]           = useState("stock");   // "stock" | "option"
  const [symbol, setSymbol]     = useState(prefillSymbol || "");
  const [side, setSide]         = useState("BUY");
  const [qty, setQty]           = useState(1);
  const [limitPrice, setLimit]  = useState("");

  // Option-specific
  const [expiries, setExpiries]     = useState([]);
  const [expiry, setExpiry]         = useState("");
  const [cepe, setCepe]             = useState("CE");
  const [lots, setLots]             = useState(1);
  const [ltp, setLtp]               = useState(null);
  const [ltpLoading, setLtpLoading] = useState(false);
  const [contracts, setContracts]   = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [strike, setStrike]         = useState("");
  const [selectedContract, setSelContract] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]         = useState(null);   // {orders:[]}
  const [error, setError]           = useState("");

  // Load expiries once on open
  useEffect(() => {
    if (!isOpen) return;
    axios.get("/expiries").then(r => {
      const exp = r.data.expiries || [];
      setExpiries(exp);
      if (exp.length && !expiry) setExpiry(exp[0]);
    }).catch(() => {});
  }, [isOpen]);

  // Reset when prefill changes
  useEffect(() => {
    setSymbol(prefillSymbol || "");
    setLtp(null); setContracts([]); setStrike(""); setSelContract(null);
    setResult(null); setError("");
  }, [prefillSymbol, isOpen]);

  // Fetch LTP when symbol changes for option tab
  const fetchLtp = useCallback(async (sym) => {
    if (!sym || tab !== "option") return;
    setLtpLoading(true); setLtp(null); setContracts([]); setStrike(""); setSelContract(null);
    try {
      const r = await axios.get("/trading/ltp", { params: { symbol: sym, username } });
      setLtp(r.data.ltp);
    } catch { setLtp(null); }
    finally { setLtpLoading(false); }
  }, [tab, username]);

  useEffect(() => { if (symbol && tab === "option") fetchLtp(symbol); }, [symbol, tab]);

  // Fetch contracts when ltp / expiry / cepe changes
  useEffect(() => {
    if (!ltp || !expiry || !symbol || tab !== "option") return;
    setContractsLoading(true); setContracts([]); setStrike(""); setSelContract(null);
    axios.get("/options/snapshot", { params: { username, stock_name: symbol, expiry, ce_pe: cepe, underlying_ltp: ltp } })
      .then(r => setContracts(r.data.contracts || []))
      .catch(() => setContracts([]))
      .finally(() => setContractsLoading(false));
  }, [ltp, expiry, cepe, symbol, tab, username]);

  const handleStrike = (val) => {
    setStrike(val);
    setSelContract(contracts.find(c => String(c.strike) === val) || null);
  };

  const submit = async () => {
    setError(""); setResult(null);
    const sym = symbol.trim().toUpperCase();
    if (!sym) { setError("Symbol is required."); return; }

    let order;
    if (tab === "stock") {
      if (!qty || qty < 1) { setError("Qty must be ≥ 1."); return; }
      order = {
        username, underlying_stock: sym, transactiontype: side,
        lots: Number(qty), pe_ce: "ST", strikeprice: 0, expiry: "",
        buyprice:  side === "BUY"  && limitPrice ? Number(limitPrice) : 0,
        sellprice: side === "SELL" && limitPrice ? Number(limitPrice) : 0,
      };
    } else {
      if (!strike || !selectedContract) { setError("Select a strike."); return; }
      if (!lots || lots < 1) { setError("Lots must be ≥ 1."); return; }
      order = {
        username, underlying_stock: sym, transactiontype: side,
        lots: Number(lots), pe_ce: cepe, strikeprice: Number(strike), expiry,
        buyprice:  side === "BUY"  && limitPrice ? Number(limitPrice) : 0,
        sellprice: side === "SELL" && limitPrice ? Number(limitPrice) : 0,
      };
    }

    setSubmitting(true);
    try {
      const res = await axios.post("/place_orders_bulk", { orders: [order] }, { withCredentials: true });
      setResult(res.data.orders || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  const reset = () => {
    setResult(null); setError(""); setSymbol(""); setSide("BUY"); setQty(1); setLimit("");
    setLtp(null); setContracts([]); setStrike(""); setSelContract(null); setLots(1);
  };

  if (!isOpen) return null;

  const allSucceeded = result && result.every(o => o.status !== "FAILED");
  const fmtLtp = (v) => v != null ? formatCurrency(Number(v), currencySymbol) : "—";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} />
      {/* Dialog */}
      <div style={{ position: "relative", background: "#fff", borderRadius: 14, width: "min(600px, 95vw)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,.25)", padding: "24px 28px" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#2c3e50" }}>New Trade</div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1 }}>✕</button>
        </div>

        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {[["stock", "Stock"], ["option", "Option"]].map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setResult(null); setError(""); setContracts([]); setStrike(""); setSelContract(null); }}
              style={{ padding: "7px 20px", borderRadius: 8, border: "2px solid", cursor: "pointer", fontWeight: 600, fontSize: 13,
                background: tab === key ? "#0055aa" : "#fff",
                color:      tab === key ? "#fff"    : "#555",
                borderColor: tab === key ? "#0055aa" : "#ddd" }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Stock form ── */}
        {tab === "stock" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Symbol</label>
              <SymbolSearch value={symbol} onChange={setSymbol} />
            </div>
            <div>
              <label style={lbl}>Action</label>
              <SideToggle value={side} onChange={setSide} />
            </div>
            <div>
              <label style={lbl}>Qty (shares)</label>
              <input type="number" min="1" value={qty} onChange={e => setQty(e.target.value)} style={iStyle} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Limit Price <span style={{ color: "#aaa", fontWeight: 400 }}>(optional — leave blank for MARKET)</span></label>
              <input type="number" min="0" step="0.01" placeholder="market" value={limitPrice} onChange={e => setLimit(e.target.value)} style={iStyle} />
            </div>
          </div>
        )}

        {/* ── Option form ── */}
        {tab === "option" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Underlying Symbol</label>
              <SymbolSearch value={symbol} onChange={(v) => { setSymbol(v); setContracts([]); setStrike(""); setSelContract(null); }} />
            </div>

            {/* LTP chip */}
            {symbol && (
              <div style={{ gridColumn: "1/-1", display: "flex", alignItems: "center", gap: 10 }}>
                {ltpLoading
                  ? <span style={{ fontSize: 12, color: "#888" }}>Fetching price…</span>
                  : ltp
                    ? <span style={{ fontSize: 13, color: "#28a745", fontWeight: 600 }}>LTP: {fmtLtp(ltp)}</span>
                    : <span style={{ fontSize: 12, color: "#dc3545" }}>Could not fetch price for {symbol}</span>
                }
              </div>
            )}

            <div>
              <label style={lbl}>Type</label>
              <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 6, overflow: "hidden" }}>
                {["CE", "PE"].map(t => (
                  <button key={t} onClick={() => setCepe(t)}
                    style={{ flex: 1, padding: "7px 0", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13,
                      background: cepe === t ? (t === "CE" ? "#17a2b8" : "#fd7e14") : "#fff",
                      color: cepe === t ? "#fff" : "#555" }}>{t === "CE" ? "Call" : "Put"}</button>
                ))}
              </div>
            </div>

            <div>
              <label style={lbl}>Expiry</label>
              <select value={expiry} onChange={e => setExpiry(e.target.value)} style={iStyle}>
                {expiries.map(exp => (
                  <option key={exp} value={exp}>
                    {new Date(exp + "T00:00:00").toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>
                Strike {contractsLoading && <span style={{ color: "#888", fontWeight: 400 }}>loading…</span>}
              </label>
              <select value={strike} onChange={e => handleStrike(e.target.value)} disabled={contractsLoading || !contracts.length} style={iStyle}>
                <option value="">— select strike —</option>
                {contracts.map(c => (
                  <option key={c.strike} value={String(c.strike)}>
                    ${c.strike} · Δ {c.delta > 0 ? "+" : ""}{c.delta} · LTP ${c.ltp}
                  </option>
                ))}
              </select>
              {!ltp && symbol && !ltpLoading && <div style={{ fontSize: 11, color: "#dc3545", marginTop: 3 }}>Need a valid symbol with live price to load strikes.</div>}
            </div>

            {/* Greeks chip */}
            {selectedContract && (
              <div style={{ gridColumn: "1/-1", display: "flex", gap: 10 }}>
                {[["LTP", formatCurrency(selectedContract.ltp, currencySymbol)], ["Delta", selectedContract.delta], ["Theta", selectedContract.theta], ["OI", selectedContract.oi?.toLocaleString()]].map(([k, v]) => (
                  <div key={k} style={{ padding: "4px 10px", background: "#f8f9ff", border: "1px solid #e0e4f5", borderRadius: 6, fontSize: 12 }}>
                    <div style={{ color: "#888", fontSize: 10 }}>{k}</div>
                    <div style={{ fontWeight: 700 }}>{v ?? "—"}</div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label style={lbl}>Action</label>
              <SideToggle value={side} onChange={setSide} />
            </div>
            <div>
              <label style={lbl}>Lots</label>
              <input type="number" min="1" value={lots} onChange={e => setLots(e.target.value)} style={iStyle} />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={lbl}>Limit Price <span style={{ color: "#aaa", fontWeight: 400 }}>(optional)</span></label>
              <input type="number" min="0" step="0.01" placeholder="market" value={limitPrice} onChange={e => setLimit(e.target.value)} style={iStyle} />
            </div>
          </div>
        )}

        {/* Error */}
        {error && <div style={{ marginTop: 14, padding: "8px 12px", background: "#fff0f0", color: "#dc3545", borderRadius: 6, fontSize: 13, fontWeight: 600 }}>{error}</div>}

        {/* Result */}
        {result && (
          <div style={{ marginTop: 14, border: "1px solid #eee", borderRadius: 8, overflow: "hidden" }}>
            {result.map((o, i) => (
              <div key={i} style={{ padding: "10px 14px", background: o.status === "FAILED" ? "#fff0f0" : "#f0fff4", borderBottom: i < result.length - 1 ? "1px solid #eee" : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700 }}>{o.tradingsymbol}</span>
                  <span style={{ fontWeight: 700, color: o.transactiontype === "BUY" ? "#28a745" : "#dc3545" }}>{o.transactiontype}</span>
                  <span style={{ fontSize: 12, color: "#555" }}>Qty: {o.quantity || "—"}</span>
                  <span style={{
                    padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                    background: o.status === "FAILED" ? "#dc354520" : "#28a74520",
                    color:      o.status === "FAILED" ? "#dc3545"   : "#28a745",
                  }}>{o.status}</span>
                  {o.orderid && <span style={{ fontFamily: "monospace", fontSize: 11, color: "#888" }}>#{o.orderid}</span>}
                </div>
                {o.error && <div style={{ marginTop: 4, fontSize: 12, color: "#c0392b" }}>{o.error}</div>}
              </div>
            ))}
          </div>
        )}

        {/* Footer buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          {result ? (
            <>
              <button onClick={reset} style={btnSecondary}>New Trade</button>
              <button onClick={onClose} style={btnPrimary}>Close</button>
            </>
          ) : (
            <>
              <button onClick={onClose} style={btnSecondary}>Cancel</button>
              <button onClick={submit} disabled={submitting} style={{ ...btnPrimary, background: side === "BUY" ? "#28a745" : "#dc3545", opacity: submitting ? 0.7 : 1 }}>
                {submitting ? "Placing…" : `${side} ${tab === "stock" ? (symbol || "Stock") : (symbol || "Option")}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SideToggle({ value, onChange }) {
  return (
    <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 6, overflow: "hidden" }}>
      {["BUY", "SELL"].map(s => (
        <button key={s} onClick={() => onChange(s)}
          style={{ flex: 1, padding: "7px 0", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13,
            background: value === s ? (s === "BUY" ? "#28a745" : "#dc3545") : "#fff",
            color: value === s ? "#fff" : "#555" }}>{s}</button>
      ))}
    </div>
  );
}

const iStyle    = { padding: "7px 10px", borderRadius: 6, border: "1px solid #ccc", fontSize: 13, width: "100%", boxSizing: "border-box" };
const lbl       = { fontSize: 11, fontWeight: 700, color: "#6c757d", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" };
const btnPrimary   = { padding: "8px 20px", borderRadius: 8, border: "none", background: "#0055aa", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "8px 20px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };

const fmt = (n, currencySymbol) =>
  n == null ? "—" : formatCurrency(Math.abs(n), currencySymbol, 0);
const pnlColor = (v) => (v >= 0 ? "#28a745" : "#dc3545");
const pnlFmt   = (v, currencySymbol) =>
  v == null ? "—" : formatCurrencySigned(v, currencySymbol, 0);
// Handles MM/DD/YYYY (from intent.expiry) → "May 25"
const fmtExpMonth = (d) => {
  if (!d) return "—";
  const parts = d.split("/");
  if (parts.length !== 3) return d;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const m = parseInt(parts[0], 10) - 1;
  const y = parts[2].slice(-2);
  return `${months[m] || "?"} ${y}`;
};
const distColor = (v) => v == null ? "#888" : v < 0 ? "#dc3545" : v < 1.5 ? "#fd7e14" : "#28a745";
const fmtDist   = (v) => { if (v == null) return "—"; return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`; };

/* ── All-accounts positions view ── */
const AllAccountsPositions = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get("/dashboard/all-summary")
      .then(r => setSummaries(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spinner color="primary" /></div>;

  const totalStockPnl   = summaries.reduce((a, s) => a + s.stock_pnl, 0);
  const totalOptionPnl  = summaries.reduce((a, s) => a + s.fo_unrealised_pnl, 0);
  const totalPotential  = summaries.reduce((a, s) => a + s.potential_pnl, 0);
  const totalShort      = summaries.reduce((a, s) => a + s.short_option_value, 0);
  const totalCash       = summaries.reduce((a, s) => a + (s.available_cash || 0), 0);

  return (
    <div style={{ padding: "0 25px 30px" }}>
      {/* Aggregate strip */}
      <Row className="mb-4">
        {[
          { label: "Total Available Cash", value: fmt(totalCash),  color: "#28a745" },
          { label: "Combined P&L",         value: pnlFmt(totalStockPnl + totalOptionPnl), color: pnlColor(totalStockPnl + totalOptionPnl) },
          { label: "Total Stock P&L",      value: pnlFmt(totalStockPnl),  color: pnlColor(totalStockPnl) },
          { label: "Total F&O P&L",        value: pnlFmt(totalOptionPnl), color: pnlColor(totalOptionPnl) },
          { label: "Total Potential P&L",  value: pnlFmt(totalPotential), color: pnlColor(totalPotential) },
          { label: "Short Option Value",   value: fmt(totalShort), color: "#6f42c1" },
        ].map(c => (
          <Col key={c.label}>
            <Card className="mb-2" style={{ borderRadius: 10, border: "1px solid #eee", textAlign: "center" }}>
              <CardBody style={{ padding: "12px 10px" }}>
                <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.color }}>{c.value}</div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Per-account table */}
      <Card style={{ borderRadius: 12, border: "1px solid #eee" }}>
        <CardBody style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8f9fb" }}>
                  {["Account", "Broker", "Stocks", "Stock P&L", "F&O P&L", "Potential P&L", "Short Value", "Available Cash", "Used Margin", "Options", "Strategies", "Rules"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", color: "#555", fontWeight: 600, whiteSpace: "nowrap", borderBottom: "2px solid #eee" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summaries.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign: "center", padding: 32, color: "#aaa" }}>No accounts found.</td></tr>
                )}
                {summaries.map(s => (
                  <tr key={s.username} style={{ borderTop: "1px solid #f0f0f0" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "10px 14px", fontWeight: 700 }}>{s.username}</td>
                    <td style={{ padding: "10px 14px", color: "#888" }}>{s.broker}</td>
                    <td style={{ padding: "10px 14px" }}>{s.stock_count}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: pnlColor(s.stock_pnl) }}>{pnlFmt(s.stock_pnl)}</td>
                    <td style={{ padding: "10px 14px", color: pnlColor(s.fo_unrealised_pnl) }}>{pnlFmt(s.fo_unrealised_pnl)}</td>
                    <td style={{ padding: "10px 14px", color: pnlColor(s.potential_pnl) }}>{pnlFmt(s.potential_pnl)}</td>
                    <td style={{ padding: "10px 14px", color: "#6f42c1" }}>{fmt(s.short_option_value)}</td>
                    <td style={{ padding: "10px 14px", fontWeight: 600, color: s.available_cash != null ? "#28a745" : "#aaa" }}>
                      {s.available_cash != null ? fmt(s.available_cash) : "—"}
                    </td>
                    <td style={{ padding: "10px 14px", color: s.used_margin != null ? "#dc3545" : "#aaa" }}>
                      {s.used_margin != null ? fmt(s.used_margin) : "—"}
                    </td>
                    <td style={{ padding: "10px 14px" }}>{s.option_positions}</td>
                    <td style={{ padding: "10px 14px" }}>{s.active_strategies > 0 ? <Badge color="success">{s.active_strategies} active</Badge> : "—"}</td>
                    <td style={{ padding: "10px 14px" }}>{s.active_rules > 0 ? <Badge color="primary">{s.active_rules}</Badge> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

// All toggleable columns. Keys match the stock object fields.
const TOGGLE_COLS = [
  { key: "expiry",        label: "Exp Month" },
  { key: "ltp",           label: "LTP" },
  { key: "qty",           label: "Qty (Lots)" },
  { key: "avg_price",     label: "Avg Price" },
  { key: "invested",      label: "Invested" },
  { key: "stock_pnl",     label: "Stock P&L" },
  { key: "option_pnl",    label: "F&O P&L" },
  { key: "potential_pnl", label: "Potential P&L" },
  { key: "premium_rec",   label: "Premium Rec." },
  { key: "options",       label: "Options" },
  { key: "strategy",      label: "Strategy" },
  { key: "bullish_delta",    label: "Bullish Δ" },
  { key: "bearish_delta",    label: "Bearish Δ" },
  { key: "delta_diff",       label: "Delta Diff" },
  { key: "nearest_put_pct",  label: "Nearest Put %" },
  { key: "nearest_call_pct", label: "Nearest Call %" },
  { key: "booked_profit",    label: "Booked Profit" },
];

const DEFAULT_HIDDEN = new Set(["bullish_delta", "bearish_delta", "delta_diff"]);
const DEFAULT_COL_ORDER = TOGGLE_COLS.map(c => c.key);

const PortfolioDashboard = () => {
  const { selectedAccount, currencySymbol } = useAccount();
  const isAllAccounts = selectedAccount === "All Accounts";
  const history = useHistory();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [balance, setBalance]   = useState(null);
  const [filter, setFilter]     = useState("");
  const [tradeModal, setTradeModal] = useState({ open: false, symbol: "" });
  const [sort, setSort]         = useState({ col: "stock_pnl", dir: "desc" });
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const saved = localStorage.getItem("portfolio_hidden_cols");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_) {}
    return DEFAULT_HIDDEN;
  });
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("portfolio_col_order");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge: keep saved order, append any new columns not in saved
        const known = new Set(parsed);
        const merged = [...parsed, ...DEFAULT_COL_ORDER.filter(k => !known.has(k))];
        return merged;
      }
    } catch (_) {}
    return DEFAULT_COL_ORDER;
  });
  const [colPickerOpen, setColPickerOpen] = useState(false);

  // Drag-and-drop refs
  const dragKey = useRef(null);
  const dragOver = useRef(null);

  const onDragStart = (key) => { dragKey.current = key; };
  const onDragEnter = (key) => { dragOver.current = key; };
  const onDragEnd   = () => {
    if (!dragKey.current || !dragOver.current || dragKey.current === dragOver.current) return;
    setColOrder(prev => {
      const next = [...prev];
      const from = next.indexOf(dragKey.current);
      const to   = next.indexOf(dragOver.current);
      if (from === -1 || to === -1) return prev;
      next.splice(from, 1);
      next.splice(to, 0, dragKey.current);
      localStorage.setItem("portfolio_col_order", JSON.stringify(next));
      return next;
    });
    dragKey.current  = null;
    dragOver.current = null;
  };

  const toggleCol = (key) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem("portfolio_hidden_cols", JSON.stringify([...next]));
      return next;
    });
  };
  const show = (key) => !hiddenCols.has(key);

  const cycleSort = (col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === "desc" ? "asc" : "desc" } : { col, dir: "desc" });
  };
  const sortArrow = (col) => sort.col === col ? (sort.dir === "desc" ? " ▼" : " ▲") : " ↕";

  useEffect(() => {
    if (!selectedAccount || isAllAccounts) return;
    setLoading(true);
    axios.get("/dashboard/summary", { params: { username: selectedAccount } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    // Fetch trading balance from broker (non-blocking)
    axios.get("/dashboard/balance", { params: { username: selectedAccount } })
      .then(r => setBalance(r.data))
      .catch(() => setBalance(null));
  }, [selectedAccount]);

  const stocks = (data?.stocks || [])
    .filter(s => s.stock_name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => {
      let av, bv;
      if (sort.col === "stock_name")    { av = a.stock_name;    bv = b.stock_name;    return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sort.col === "strategy_type") { av = a.strategy_type || ""; bv = b.strategy_type || ""; return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av); }
      if (sort.col === "stock_pnl")         { av = a.stock_pnl;         bv = b.stock_pnl; }
      if (sort.col === "option_pnl")        { av = a.fo_unrealised_pnl  ?? (sort.dir === "desc" ? -Infinity : Infinity); bv = b.fo_unrealised_pnl  ?? (sort.dir === "desc" ? -Infinity : Infinity); }
      if (sort.col === "qty")               { av = a.quantity ?? 0;      bv = b.quantity ?? 0; }
      if (sort.col === "invested")          { av = a.invested;           bv = b.invested; }
      if (sort.col === "nearest_put_pct")   { av = a.nearest_put_pct  ?? Infinity; bv = b.nearest_put_pct  ?? Infinity; }
      if (sort.col === "nearest_call_pct")  { av = a.nearest_call_pct ?? Infinity; bv = b.nearest_call_pct ?? Infinity; }
      if (sort.col === "booked_profit")     { av = a.booked_profit ?? (sort.dir === "desc" ? -Infinity : Infinity); bv = b.booked_profit ?? (sort.dir === "desc" ? -Infinity : Infinity); }
      if (sort.col === "expiry") {
        // MM/DD/YYYY → sortable YYYYMM string
        const toYYYYMM = (d) => { if (!d) return ""; const p = d.split("/"); return p.length === 3 ? p[2] + p[0] : ""; };
        return sort.dir === "asc" ? toYYYYMM(a.expiry).localeCompare(toYYYYMM(b.expiry)) : toYYYYMM(b.expiry).localeCompare(toYYYYMM(a.expiry));
      }
      return sort.dir === "asc" ? av - bv : bv - av;
    });

  const { option_metrics, totals } = data || {};

  return (
    <Fragment>
      <PageTitleAlt2
        heading="Portfolio Positions"
        subheading={isAllAccounts ? "Positions across all accounts" : "All stock positions with live P&L and option summary"}
        icon="pe-7s-note2 icon-gradient bg-sunny-morning"
      />

      {isAllAccounts ? (
        <AllAccountsPositions />
      ) : (
      <div style={{ padding: "0 25px 30px" }}>

        {/* ── Trading balance strip — always visible, loads independently ── */}
        <Row className="mb-2">
          {balance && balance.available_cash != null ? (
            <>
              <Col md={4}>
                <Card style={{ borderRadius: 10, border: "2px solid #28a745", background: "#f6fff8" }}>
                  <CardBody style={{ padding: "10px 16px" }}>
                    <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", marginBottom: 2 }}>Available Cash</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#28a745" }}>{fmt(balance.available_cash, currencySymbol)}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card style={{ borderRadius: 10, border: "1px solid #eee" }}>
                  <CardBody style={{ padding: "10px 16px" }}>
                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 2 }}>Used Margin</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#dc3545" }}>{fmt(balance.used_margin, currencySymbol)}</div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4}>
                <Card style={{ borderRadius: 10, border: "1px solid #eee" }}>
                  <CardBody style={{ padding: "10px 16px" }}>
                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 2 }}>Total Margin</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#495057" }}>{fmt(balance.total_margin, currencySymbol)}</div>
                  </CardBody>
                </Card>
              </Col>
            </>
          ) : (
            <Col>
              <Card style={{ borderRadius: 10, border: "1px solid #eee", background: "#fafafa" }}>
                <CardBody style={{ padding: "10px 16px" }}>
                  <div style={{ fontSize: 12, color: "#aaa" }}>
                    Trading balance unavailable (broker offline or session expired)
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>

        {/* ── P&L Summary strip ── */}
        {data && (
          <Row className="mb-3">
            {[
              { label: "Stock P&L",     value: pnlFmt(totals.stock_pnl, currencySymbol),                color: pnlColor(totals.stock_pnl) },
              { label: "F&O P&L",       value: pnlFmt(option_metrics.fo_unrealised_pnl, currencySymbol),  color: pnlColor(option_metrics.fo_unrealised_pnl) },
              { label: "Potential P&L", value: pnlFmt(option_metrics.potential_pnl, currencySymbol), color: pnlColor(option_metrics.potential_pnl) },
              { label: "Short Value",   value: fmt(option_metrics.short_option_value, currencySymbol), color: "#6f42c1" },
            ].map(c => (
              <Col md={3} key={c.label}>
                <Card className="mb-3" style={{ borderRadius: 10, border: "1px solid #eee" }}>
                  <CardBody style={{ padding: "12px 16px" }}>
                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>{c.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: c.color }}>{c.value}</div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Modal */}
        <NewTradeModal
          isOpen={tradeModal.open}
          onClose={() => setTradeModal({ open: false, symbol: "" })}
          prefillSymbol={tradeModal.symbol}
          username={selectedAccount}
        />

        {/* ── Filter + count ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter by stock name…"
            style={{ padding: "7px 12px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, width: 220 }}
          />
          <span style={{ fontSize: 13, color: "#888" }}>
            {loading ? "Loading…" : `${stocks.length} position${stocks.length !== 1 ? "s" : ""}`}
          </span>
          <button
            onClick={() => setTradeModal({ open: true, symbol: "" })}
            style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: "#0055aa", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            + New Trade
          </button>
          <div style={{ position: "relative", marginLeft: "auto" }}>
            <button
              onClick={() => setColPickerOpen(o => !o)}
              style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #ccc", fontSize: 12, cursor: "pointer", background: "#fff" }}
            >
              Columns ▾
            </button>
            {colPickerOpen && (
              <div style={{
                position: "absolute", right: 0, top: "110%", zIndex: 200,
                background: "#fff", border: "1px solid #ddd", borderRadius: 8,
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "10px 16px",
                minWidth: 180,
              }}>
                {TOGGLE_COLS.map(c => (
                  <label key={c.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                    <input type="checkbox" checked={!hiddenCols.has(c.key)} onChange={() => toggleCol(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading && <div style={{ textAlign: "center", padding: 40 }}><Spinner color="primary" /></div>}

        {/* ── Positions table ── */}
        {!loading && (
          <Card style={{ borderRadius: 12, border: "1px solid #eee" }}>
            <CardBody style={{ padding: 0 }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8f9fb" }}>
                      {/* Fixed: Stock */}
                      <th style={{ padding: "11px 14px", textAlign: "left", color: "#007bff", fontWeight: 600, whiteSpace: "nowrap", borderBottom: "2px solid #eee", cursor: "pointer", userSelect: "none" }}
                          onClick={() => cycleSort("stock_name")}>
                        Stock{sortArrow("stock_name")}
                      </th>
                      {/* Draggable middle columns in user-defined order */}
                      {colOrder.filter(k => show(k)).map(k => {
                        const def = TOGGLE_COLS.find(c => c.key === k);
                        if (!def) return null;
                        const sortCol = k === "qty" ? "qty" : k === "strategy" ? "strategy_type" : k === "option_pnl" ? "option_pnl" : ["stock_pnl","invested","nearest_put_pct","nearest_call_pct","booked_profit","expiry"].includes(k) ? k : null;
                        const isDragging = dragOver.current === k;
                        return (
                          <th key={k}
                              draggable
                              onDragStart={() => onDragStart(k)}
                              onDragEnter={() => onDragEnter(k)}
                              onDragOver={e => e.preventDefault()}
                              onDragEnd={onDragEnd}
                              onClick={sortCol ? () => cycleSort(sortCol) : undefined}
                              style={{
                                padding: "11px 14px", textAlign: "left",
                                color: sortCol ? "#007bff" : "#555",
                                fontWeight: 600, whiteSpace: "nowrap",
                                borderBottom: "2px solid #eee",
                                cursor: "grab", userSelect: "none",
                                background: isDragging ? "#e8f0fe" : undefined,
                                borderLeft: isDragging ? "2px solid #4285f4" : undefined,
                                opacity: dragKey.current === k ? 0.5 : 1,
                              }}>
                            ⠿ {def.label}{sortCol ? sortArrow(sortCol) : ""}
                          </th>
                        );
                      })}
                      {/* Fixed: Detail */}
                      <th style={{ padding: "11px 14px", borderBottom: "2px solid #eee" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {stocks.length === 0 && (
                      <tr>
                        <td colSpan={15} style={{ textAlign: "center", padding: 32, color: "#aaa" }}>
                          No positions found.
                        </td>
                      </tr>
                    )}
                    {stocks.map(s => {
                      const fmtDelta = (v) => v == null ? "—" : v.toFixed(2);
                      const deltaColor = (v) => v == null ? "#ccc" : v >= 0 ? "#28a745" : "#dc3545";
                      return (
                      <tr
                        key={s.stock_name}
                        style={{ borderTop: "1px solid #f0f0f0" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>
                          <span
                            style={{ color: "#007bff", cursor: "pointer" }}
                            onClick={() => window.open(`/trading/${s.stock_name}`, "_blank")}
                          >
                            {s.stock_name}
                          </span>
                        </td>
                        {colOrder.filter(k => show(k)).map(k => {
                          switch (k) {
                            case "ltp":
                              return <td key={k} style={{ padding: "10px 14px" }}>{formatCurrency(s.ltp, currencySymbol)}</td>;
                            case "qty":
                              return (
                                <td key={k} style={{ padding: "10px 14px" }}>
                                  {s.quantity.toLocaleString()}
                                  {s.lots != null && <span style={{ color: "#888", fontSize: 11, marginLeft: 5 }}>({s.lots}L)</span>}
                                </td>
                              );
                            case "avg_price":
                              return <td key={k} style={{ padding: "10px 14px" }}>{formatCurrency(s.avg_price, currencySymbol)}</td>;
                            case "invested":
                              return <td key={k} style={{ padding: "10px 14px", color: "#555" }}>{fmt(s.invested, currencySymbol)}</td>;
                            case "stock_pnl":
                              return <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: pnlColor(s.stock_pnl) }}>{pnlFmt(s.stock_pnl, currencySymbol)}</td>;
                            case "option_pnl": {
                              const hasFo = s.option_count > 0 || !!s.futures_pnl;
                              return <td key={k} style={{ padding: "10px 14px", color: hasFo ? pnlColor(s.fo_unrealised_pnl) : "#ccc" }}>{hasFo ? pnlFmt(s.fo_unrealised_pnl, currencySymbol) : "—"}</td>;
                            }
                            case "potential_pnl":
                              return <td key={k} style={{ padding: "10px 14px", color: s.option_count > 0 ? pnlColor(s.potential_pnl) : "#ccc" }}>{s.option_count > 0 ? pnlFmt(s.potential_pnl, currencySymbol) : "—"}</td>;
                            case "premium_rec":
                              return <td key={k} style={{ padding: "10px 14px", color: s.option_count > 0 ? "#6f42c1" : "#ccc" }}>{s.option_count > 0 ? fmt(s.premium_received, currencySymbol) : "—"}</td>;
                            case "options":
                              return <td key={k} style={{ padding: "10px 14px" }}>{s.option_count > 0 ? <Badge color="info" pill>{s.option_count}</Badge> : <span style={{ color: "#ccc" }}>—</span>}</td>;
                            case "strategy":
                              return (
                                <td key={k} style={{ padding: "10px 14px" }}>
                                  {s.strategy_type ? (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                      <span style={{ fontSize: 11, fontWeight: 600, color: "#2c3e50", whiteSpace: "nowrap" }}>{strategyLabel(s.strategy_type)}</span>
                                      <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 8, display: "inline-block", width: "fit-content", background: s.strategy_status === "ACTIVE" ? "#d4edda" : "#e2e3e5", color: s.strategy_status === "ACTIVE" ? "#28a745" : "#6c757d" }}>{s.strategy_status}</span>
                                    </div>
                                  ) : <span style={{ color: "#ccc" }}>—</span>}
                                </td>
                              );
                            case "bullish_delta":
                              return <td key={k} style={{ padding: "10px 14px", color: deltaColor(s.bullish_delta), fontWeight: 600 }}>{s.bullish_delta != null ? `+${fmtDelta(s.bullish_delta)}` : "—"}</td>;
                            case "bearish_delta":
                              return <td key={k} style={{ padding: "10px 14px", color: deltaColor(s.bearish_delta), fontWeight: 600 }}>{fmtDelta(s.bearish_delta)}</td>;
                            case "delta_diff":
                              return <td key={k} style={{ padding: "10px 14px", color: deltaColor(s.delta_diff), fontWeight: 600 }}>{s.delta_diff != null ? (s.delta_diff >= 0 ? `+${fmtDelta(s.delta_diff)}` : fmtDelta(s.delta_diff)) : "—"}</td>;
                            case "nearest_put_pct":
                              return (
                                <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: distColor(s.nearest_put_pct) }}>
                                  {s.nearest_put_pct != null ? <span style={s.nearest_put_pct < 0 ? { background: "#fde2e2", color: "#dc3545", padding: "1px 6px", borderRadius: 6 } : undefined}>{fmtDist(s.nearest_put_pct)}</span> : "—"}
                                </td>
                              );
                            case "nearest_call_pct":
                              return (
                                <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: distColor(s.nearest_call_pct) }}>
                                  {s.nearest_call_pct != null ? <span style={s.nearest_call_pct < 0 ? { background: "#fde2e2", color: "#dc3545", padding: "1px 6px", borderRadius: 6 } : undefined}>{fmtDist(s.nearest_call_pct)}</span> : "—"}
                                </td>
                              );
                            case "booked_profit":
                              return <td key={k} style={{ padding: "10px 14px", fontWeight: 600, color: s.booked_profit != null ? pnlColor(s.booked_profit) : "#ccc" }}>{s.booked_profit != null ? pnlFmt(s.booked_profit, currencySymbol) : "—"}</td>;
                            case "expiry":
                              return <td key={k} style={{ padding: "10px 14px", color: "#555", whiteSpace: "nowrap" }}>{fmtExpMonth(s.expiry)}</td>;
                            default: return null;
                          }
                        })}
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <button
                              onClick={() => setTradeModal({ open: true, symbol: s.stock_name })}
                              title="New trade for this stock"
                              style={{ padding: "3px 10px", borderRadius: 6, border: "1px solid #0055aa", background: "#fff", color: "#0055aa", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                            >
                              Trade
                            </button>
                            <span
                              style={{ color: "#007bff", cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}
                              onClick={() => history.push(`/trading/${s.stock_name}`)}
                            >
                              Detail →
                            </span>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        )}
      </div>
      )}
    </Fragment>
  );
};

export default PortfolioDashboard;
